import { useUnit } from 'effector-react';
import { LogIn, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $session, $signInMethods, signOutRequested } from '../../entities/session';
import { routes } from '../../shared/routing';
import {
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
} from '../../shared/ui';

/**
 * "Sign in" when anonymous (only if the app has an API), or the account menu when signed in.
 * Anonymous use is complete on its own: this is an offer, never a gate.
 */
export function AccountMenu() {
  const { t } = useTranslation();
  const [session, methods, signOut, openSignIn] = useUnit([
    $session,
    $signInMethods,
    signOutRequested,
    routes.signIn.navigate,
  ]);

  if (session.status === 'unknown') return null;
  if (session.status === 'anonymous') {
    if (!methods || methods.length === 0) return null;
    return (
      <Tooltip content={t('account.signInHint')}>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<LogIn />}
          onClick={() => {
            void openSignIn({
              params: {},
              query: { returnTo: `${window.location.pathname}${window.location.search}` },
            });
          }}
        >
          {t('account.signIn')}
        </Button>
      </Tooltip>
    );
  }

  const { user } = session;
  const initial = (user.name ?? user.email).trim().charAt(0).toUpperCase();
  return (
    <Menu>
      <Tooltip content={t('account.menu')}>
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label={t('account.menu')}
            className="flex size-7 items-center justify-center rounded-full bg-accent text-xs font-semibold text-white outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-focus"
          >
            {initial}
          </button>
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="end">
        <MenuLabel>{t('account.signedInAs', { email: user.email })}</MenuLabel>
        <MenuSeparator />
        <MenuItem icon={<LogOut />} onSelect={signOut}>
          {t('account.signOut')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
